import React, { useEffect, useState, useCallback } from "react";
import {
  Box,
  Typography,
  Button,
  Checkbox,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Backdrop,
  CircularProgress,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  Snackbar,
} from "@mui/material";

import EditIcon from "@mui/icons-material/Edit";

import SubareasService from "../../../services/SubareasService";
import ModeracionService from "../../../services/ModeracionService";

import "./style_steps/step2.css";

const Paso2 = ({ selectores, setSubareas, setTemas, onNext, onBack }) => {
  const buildSelectorKey = (s) => `${s.nivel_id}-${s.grado_id}-${s.area_id}`;

  const [listaSubareas, setListaSubareas] = useState([]);
  const [seleccionadas, setSeleccionadas] = useState([]);
  const [loading, setLoading] = useState(false);

  const [openModal, setOpenModal] = useState(false);
  const [editSubareaId, setEditSubareaId] = useState(null);
  const [editSubareaValue, setEditSubareaValue] = useState("");

  const nSubAreas = 10;
  const LIMITE = 5;

  // ================================
  // Snackbar (mensaje bonito, sin alert)
  // ================================
  const [snack, setSnack] = useState({
    open: false,
    severity: "error",
    message: "",
  });

  const showSnack = (message, severity = "error") => {
    setSnack({ open: true, severity, message });
  };

  const closeSnack = (_, reason) => {
    if (reason === "clickaway") return;
    setSnack((p) => ({ ...p, open: false }));
  };

  const getModeracionMessage = (err) => {
    const detail = err?.response?.data?.detail;
    return detail?.message || "Contenido no permitido (malas palabras o +18).";
  };

  const invalidateTemas = useCallback(() => {
    if (typeof setTemas === "function") setTemas({});
    localStorage.removeItem("temasPaso3");
    localStorage.removeItem("temasPaso3_key");
  }, [setTemas]);

  useEffect(() => {
    const currentKey = buildSelectorKey(selectores);

    let savedPaso2 = null;
    let savedKey = null;

    try {
      savedPaso2 = JSON.parse(localStorage.getItem("seleccionPaso2"));
      savedKey = localStorage.getItem("seleccionPaso2_key");
    } catch {
      savedPaso2 = null;
      savedKey = null;
    }

    if (savedPaso2 && savedKey === currentKey && Array.isArray(savedPaso2.listaSubareas)) {
      setListaSubareas(savedPaso2.listaSubareas || []);
      setSeleccionadas(savedPaso2.seleccionadas || []);

      const seleccionFinal = savedPaso2.listaSubareas.filter((s) =>
        (savedPaso2.seleccionadas || []).includes(s.id)
      );
      setSubareas(seleccionFinal);
      return;
    }

    localStorage.removeItem("seleccionPaso2");
    localStorage.removeItem("seleccionPaso2_key");
    invalidateTemas();

    const obtenerSubareas = async () => {
      try {
        setLoading(true);

        const res = await SubareasService.generarSubareas(
          nSubAreas,
          selectores.nivel_id,
          selectores.grado_id,
          selectores.area_id
        );

        const subareasConId = (res.resultado || []).map((sub, idx) => ({
          ...sub,
          id: idx + 1,
        }));

        setListaSubareas(subareasConId);
        setSeleccionadas([]);
        setSubareas([]);

        localStorage.setItem(
          "seleccionPaso2",
          JSON.stringify({
            listaSubareas: subareasConId,
            seleccionadas: [],
            subareas: [],
            temasPorSubarea: {},
            generado: false,
          })
        );
        localStorage.setItem("seleccionPaso2_key", currentKey);
      } finally {
        setLoading(false);
      }
    };

    obtenerSubareas();
  }, [selectores, setSubareas, invalidateTemas]);

  const toggle = (subareaId) => {
    invalidateTemas();

    setSeleccionadas((prev) => {
      let nuevas;

      if (prev.includes(subareaId)) nuevas = prev.filter((id) => id !== subareaId);
      else if (prev.length < LIMITE) nuevas = [...prev, subareaId];
      else return prev;

      const seleccionFinal = listaSubareas.filter((s) => nuevas.includes(s.id));
      setSubareas(seleccionFinal);

      const saved = JSON.parse(localStorage.getItem("seleccionPaso2")) || {};
      localStorage.setItem(
        "seleccionPaso2",
        JSON.stringify({
          ...saved,
          listaSubareas,
          seleccionadas: nuevas,
          subareas: seleccionFinal,
          temasPorSubarea: {},
          generado: false,
        })
      );

      localStorage.setItem("seleccionPaso2_key", buildSelectorKey(selectores));

      return nuevas;
    });
  };

  /* ==========================
     EDICIÓN
  ========================== */
  const abrirEdicion = (sub) => {
    setEditSubareaId(sub.id);
    setEditSubareaValue(sub.sub_area);
    setOpenModal(true);
  };

  // ✅ Guardar edición con moderación + snack (sin cambiar el diseño)
  const guardarEdicion = async () => {
    const value = editSubareaValue.trim();
    if (!value) return;

    // 1) validar moderación
    try {
      // Reutilizamos el endpoint existente: validamos este texto como "tema"
      await ModeracionService.validarContenido({
        tema: value,
        objetivo: "",
        sub_area_tematica: value,
      });
    } catch (err) {
      showSnack(getModeracionMessage(err), "error");
      return; // ⛔ No guardar
    }

    // 2) tu lógica actual (sin cambios de diseño)
    invalidateTemas(); 
    
    const updated = listaSubareas.map((s) => {
      if (s.id !== editSubareaId) return s;

      const { prioridad, ...rest } = s;
      return { ...rest, sub_area: value };
    });

    setListaSubareas(updated);

    const seleccionFinal = updated.filter((s) => seleccionadas.includes(s.id));
    setSubareas(seleccionFinal);

    const savedPaso2 = JSON.parse(localStorage.getItem("seleccionPaso2")) || {};

    localStorage.setItem(
      "seleccionPaso2",
      JSON.stringify({
        ...savedPaso2,
        listaSubareas: updated,
        seleccionadas,
        subareas: seleccionFinal,
        temasPorSubarea: {},
        generado: false,
      })
    );

    localStorage.removeItem("temasPaso3");
    localStorage.removeItem("temasPaso3_key");

    setOpenModal(false);
    setEditSubareaId(null);
    setEditSubareaValue("");

    showSnack("Subárea actualizada correctamente.", "success");
  };

  const handleContinuar = () => {
    const seleccionFinal = listaSubareas.filter((s) => seleccionadas.includes(s.id));
    setSubareas(seleccionFinal);
    onNext();
  };

  return (
    <Box className="sae-step2-root">
        <Typography variant="h4" className="sae-step2-subtitle">
          Selecciona hasta 5 subáreas
        </Typography>

      <TableContainer component={Paper} className="sae-step2-table" sx={{ mt: 2 }}>
        <Table>
          <TableHead className="sae-step2-thead">
            <TableRow>
              <TableCell>Seleccionar</TableCell>
              <TableCell>Subárea</TableCell>
              <TableCell align="center">Acciones</TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {listaSubareas.map((sub) => {
              const checked = seleccionadas.includes(sub.id);
              const disabled = !checked && seleccionadas.length >= LIMITE;

              return (
                <TableRow
                  key={sub.id}
                  hover
                  onClick={() => (!disabled || checked) && toggle(sub.id)}
                  className="sae-step2-tr"
                  sx={{ cursor: disabled && !checked ? "not-allowed" : "pointer" }}
                >
                  <TableCell className="sae-step2-td" padding="checkbox">
                    <Checkbox
                      className="sae-step2-checkbox"
                      checked={checked}
                      disabled={disabled}
                      onClick={(e) => e.stopPropagation()}
                      onChange={(e) => {
                        e.stopPropagation();
                        toggle(sub.id);
                      }}
                      inputProps={{
                        "aria-label": `Seleccionar subárea ${sub.sub_area}`,
                      }}
                    />
                  </TableCell>

                  <TableCell className="sae-step2-td sae-step2-td-subarea">
                    <Typography variant="subtitle1" className="sae-step2-subarea-title">
                      {sub.sub_area}
                    </Typography>
                    {sub.prioridad && (
                      <Typography variant="body2" className="sae-step2-subarea-priority">
                        Prioridad: {sub.prioridad}
                      </Typography>
                    )}
                  </TableCell>

                  <TableCell align="center">
                    <IconButton
                      onClick={(e) => {
                        e.stopPropagation();
                        abrirEdicion(sub);
                      }}
                    >
                      <EditIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      <Box className="sae-step2-actions">
        <Button
          variant="outlined"
          onClick={onBack}
          className="sae-step2-btn sae-step2-btn-back"
        >
          Atrás
        </Button>

        <Button
          variant="contained"
          onClick={handleContinuar}
          disabled={seleccionadas.length === 0}
          className="sae-step2-btn sae-step2-btn-next"
        >
          Continuar
        </Button>
      </Box>

      {/* MODAL EDITAR */}
      <Dialog open={openModal} onClose={() => setOpenModal(false)} fullWidth>
        <DialogTitle>Editar subárea</DialogTitle>
        <DialogContent sx={{ overflow: "visible" }}>
          <TextField
            fullWidth
            label="Subárea"
            margin="dense"
            value={editSubareaValue}
            onChange={(e) => setEditSubareaValue(e.target.value)}
            InputLabelProps={{ shrink: true }}
            autoFocus
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenModal(false)}>Cancelar</Button>
          <Button
            variant="contained"
            onClick={guardarEdicion}
            disabled={!editSubareaValue.trim()}
          >
            Guardar
          </Button>
        </DialogActions>
      </Dialog>

      <Backdrop open={loading} sx={{ color: "#fff", zIndex: (t) => t.zIndex.drawer + 1 }}>
        <CircularProgress color="inherit" />
      </Backdrop>

      {/* SNACKBAR (mensaje bonito, sin afectar diseño) */}
      <Snackbar
        open={snack.open}
        autoHideDuration={4500}
        onClose={closeSnack}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={closeSnack}
          severity={snack.severity}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {snack.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Paso2;
